import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import { getAppAgentById } from '../services/api';

const API = '/api';
const fmtDate = d => d ? new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' }) : 'N/A';
const initials = name => (name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const StatBox = ({ label, value, color }) => (
  <div style={{ background:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'14px 16px', textAlign:'center' }}>
    <div style={{ fontSize:22, fontWeight:700, color: color||'#F5A623' }}>{value ?? 0}</div>
    <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:4 }}>{label}</div>
  </div>
);

function AgentModal({ id, token, onClose, onDelete, toast }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [suspending, setSuspending] = useState(false);

  useEffect(() => {
    getAppAgentById(token, id)
      .then(d => setData(d.data || d))
      .catch(error => toast?.(error.message, 'error'))
      .finally(() => setLoading(false));
  }, [id, token, toast]);

  const toggleSuspend = async () => {
    if (!data) return;
    setSuspending(true);
    try {
      const res = await fetch(`${API}/agents/${id}/status`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !data.isActive }),
      });
      const d = await res.json();
      if (d.success) { toast?.(`Agent ${data.isActive ? 'suspended' : 'activated'}`); setData(prev => ({ ...prev, isActive: !prev.isActive })); }
    } catch(e) { toast?.(e.message, 'error'); }
    finally { setSuspending(false); }
  };

  const deleteAgent = async () => {
    if (!data) return;
    if (!window.confirm(`Permanently delete ${data.name}? This cannot be undone.`)) return;
    setSuspending(true);
    try {
      const res = await fetch(`${API}/agents/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const d = await res.json();
      if (d.success !== false) { toast?.('Agent deleted'); onDelete?.(); onClose(); }
      else toast?.(d.message || 'Delete failed', 'error');
    } catch(e) { toast?.(e.message, 'error'); }
    finally { setSuspending(false); }
  };

  const tabs = ['overview','affiliations','requests','pushed','shortlisted','players','payouts'];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'flex-start', justifyContent:'center', zIndex:1000, padding:'24px 16px', overflowY:'auto' }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:'#0d1a1a', borderRadius:16, width:'100%', maxWidth:780, border:'1px solid rgba(255,255,255,0.07)', marginBottom:32 }}>
        <div style={{ padding:'20px 24px', borderBottom:'1px solid rgba(255,255,255,0.07)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          {loading ? <div style={{ color:'var(--text-dim)' }}>Loading...</div> : data ? (
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:48, height:48, borderRadius:'50%', background:'linear-gradient(135deg,#F5A623,#c47d0a)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, fontWeight:700, color:'#000', flexShrink:0 }}>
                {data.pfp ? <img src={data.pfp} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials(data.name)}
              </div>
              <div>
                <div style={{ fontSize:17, fontWeight:700, color:'#fff' }}>{data.name}</div>
                <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginTop:2 }}>{data.email} · Agent · {data.licenseVerificationStatus}</div>
              </div>
            </div>
          ) : <div style={{ color:'#ff6b6b' }}>Failed to load</div>}
          <div style={{ display:'flex', gap:8 }}>
            {data && <button onClick={toggleSuspend} disabled={suspending} style={{ background: data.isActive?'rgba(255,80,80,0.12)':'rgba(167,244,50,0.12)', color: data.isActive?'#ff6b6b':'#A7F432', border:`1px solid ${data.isActive?'rgba(255,80,80,0.3)':'rgba(167,244,50,0.3)'}`, borderRadius:8, padding:'6px 14px', fontSize:12, cursor:'pointer', fontWeight:600 }}>{suspending?'...':data.isActive?'Suspend':'Activate'}</button>}
            {data && <button onClick={deleteAgent} disabled={suspending} style={{ background:'rgba(255,80,80,0.08)', color:'#ff6b6b', border:'1px solid rgba(255,80,80,0.25)', borderRadius:8, padding:'6px 14px', fontSize:12, cursor:'pointer', fontWeight:600 }}>Delete</button>}
            <button onClick={onClose} style={{ background:'rgba(255,255,255,0.05)', color:'rgba(255,255,255,0.5)', border:'1px solid rgba(255,255,255,0.1)', borderRadius:8, padding:'6px 12px', fontSize:13, cursor:'pointer' }}>x</button>
          </div>
        </div>
        {loading && <div style={{ padding:60, textAlign:'center', color:'rgba(255,255,255,0.3)' }}>Loading profile...</div>}
        {!loading && !data && <div style={{ padding:40, textAlign:'center', color:'#ff6b6b', fontSize:13 }}>Failed to load profile</div>}
        {!loading && data && (
          <>
            <div style={{ padding:'20px 24px', display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10 }}>
              <StatBox label="Managed Players" value={data.stats?.managedPlayers} />
              <StatBox label="Requests Posted" value={data.stats?.requestsPosted} />
              <StatBox label="Active Requests" value={data.stats?.activeRequests} color="#4B9EF5" />
              <StatBox label="Applications" value={data.stats?.totalApplications} color="#A7F432" />
              <StatBox label="Shortlisted" value={data.stats?.shortlistedPlayers} color="#9B59B6" />
              <StatBox label="Placements" value={data.stats?.placements} color="#2ECC71" />
            </div>
            {data.balance !== null && data.balance !== undefined && (
              <div style={{ margin:'0 24px 16px', background:'rgba(245,166,35,0.06)', border:'1px solid rgba(245,166,35,0.15)', borderRadius:10, padding:'14px 18px', display:'flex', gap:24, flexWrap:'wrap' }}>
                <div><div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>Balance</div><div style={{ fontSize:18, fontWeight:700, color:'#F5A623' }}>{data.currency||'USD'} {parseFloat(data.balance||0).toFixed(2)}</div></div>
                <div><div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>Total Earned</div><div style={{ fontSize:16, fontWeight:600, color:'#fff' }}>{data.currency||'USD'} {parseFloat(data.totalEarned||0).toFixed(2)}</div></div>
                <div><div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>Paid Out</div><div style={{ fontSize:16, fontWeight:600, color:'#fff' }}>{data.currency||'USD'} {parseFloat(data.totalPaidOut||0).toFixed(2)}</div></div>
              </div>
            )}
            <div style={{ padding:'0 24px', display:'flex', gap:2, borderBottom:'1px solid rgba(255,255,255,0.07)', overflowX:'auto' }}>
              {tabs.map(t => (
                <button key={t} onClick={() => setTab(t)} style={{ background:'none', border:'none', cursor:'pointer', padding:'10px 14px', fontSize:11, fontWeight:600, textTransform:'capitalize', whiteSpace:'nowrap', color: tab===t?'#F5A623':'rgba(255,255,255,0.4)', borderBottom: tab===t?'2px solid #F5A623':'2px solid transparent' }}>{t}</button>
              ))}
            </div>
            <div style={{ padding:'20px 24px 24px' }}>
              {tab === 'overview' && (
                <div style={{ background:'rgba(255,255,255,0.03)', borderRadius:10, overflow:'hidden' }}>
                  {[['Phone',data.phoneNumber],['Nationality',data.nationality],['Area',data.area],['Language',data.primaryLanguage],['License Status',data.licenseVerificationStatus],['Status',data.isActive?'Active':'Suspended'],['Joined',fmtDate(data.createdAt)]].map(([label,value],i,arr) => (
                    <div key={label} style={{ display:'flex', padding:'11px 16px', borderBottom: i<arr.length-1?'1px solid rgba(255,255,255,0.05)':'none' }}>
                      <div style={{ width:160, fontSize:12, color:'rgba(255,255,255,0.4)', flexShrink:0 }}>{label}</div>
                      <div style={{ fontSize:13, color: value?'#fff':'rgba(255,255,255,0.25)' }}>{value||'—'}</div>
                    </div>
                  ))}
                </div>
              )}
              {tab === 'overview' && data?.license && (
                <div style={{ marginTop:12, background:'rgba(255,255,255,0.03)', borderRadius:10, padding:'16px', border:'1px solid rgba(255,255,255,0.07)' }}>
                  <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginBottom:10, fontWeight:600, textTransform:'uppercase', letterSpacing:1 }}>License Document</div>
                  <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', flexWrap:'wrap', gap:10 }}>
                    <a href={data.license} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize:13, color:'#4B9EF5', textDecoration:'none', display:'flex', alignItems:'center', gap:6, background:'rgba(75,158,245,0.08)', padding:'8px 14px', borderRadius:8, border:'1px solid rgba(75,158,245,0.2)' }}>
                      🔗 View License Document
                    </a>
                    {data.licenseVerificationStatus !== 'Verified' && (
                      <div style={{ display:'flex', gap:8 }}>
                        <button onClick={async () => {
                          if (!window.confirm('Verify this license?')) return;
                          try {
                            const r = await fetch(`${API}/agents/${id}/verify-license`, {
                              method:'PATCH', headers:{'Content-Type':'application/json','Authorization':`Bearer ${token}`},
                              body: JSON.stringify({ status:'Verified' })
                            });
                            const d = await r.json();
                            if (d.success) {
                              toast?.('License verified! Agent has been notified.');
                              getAppAgentById(token, id)
                                .then(rd => setData(rd.data || rd));
                            }
                          } catch(e) { alert('Error verifying license'); }
                        }} style={{ background:'rgba(167,244,50,0.12)', color:'#A7F432', border:'1px solid rgba(167,244,50,0.3)', borderRadius:8, padding:'7px 16px', fontSize:12, cursor:'pointer', fontWeight:600 }}>
                          ✓ Verify License
                        </button>
                        <button onClick={async () => {
                          const reason = window.prompt('Rejection reason (optional):');
                          try {
                            const r = await fetch(`${API}/agents/${id}/verify-license`, {
                              method:'PATCH', headers:{'Content-Type':'application/json','Authorization':`Bearer ${token}`},
                              body: JSON.stringify({ status:'Rejected', note: reason||'' })
                            });
                            const d = await r.json();
                            if (d.success) {
                              toast?.('License rejected. Agent has been notified.');
                              getAppAgentById(token, id)
                                .then(rd => setData(rd.data || rd));
                            }
                          } catch(e) { alert('Error rejecting license'); }
                        }} style={{ background:'rgba(255,80,80,0.12)', color:'#ff6b6b', border:'1px solid rgba(255,80,80,0.3)', borderRadius:8, padding:'7px 16px', fontSize:12, cursor:'pointer', fontWeight:600 }}>
                          ✗ Reject License
                        </button>
                      </div>
                    )}
                    {data.licenseVerificationStatus === 'Verified' && (
                      <span style={{ fontSize:12, color:'#A7F432', background:'rgba(167,244,50,0.1)', padding:'6px 14px', borderRadius:20, border:'1px solid rgba(167,244,50,0.2)' }}>✓ License Verified</span>
                    )}
                  </div>
                </div>
              )}
              {tab === 'affiliations' && (
                <div>{data.affiliatedClubs?.length ? data.affiliatedClubs.map(c => (
                  <div key={c.clubId} style={{ background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.07)', borderRadius:10, padding:'14px 16px', marginBottom:10 }}>
                    <div style={{ fontWeight:600, color:'#fff' }}>{c.clubName}</div>
                    <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginTop:4 }}>{c.clubEmail} · {c.country}</div>
                    <div style={{ fontSize:11, color:'#F5A623', marginTop:4 }}>Since {fmtDate(c.createdAt)}</div>
                  </div>
                )) : <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13 }}>No affiliated clubs</div>}</div>
              )}
              {tab === 'requests' && (
                <div>{data.playerRequests?.length ? data.playerRequests.map(r => (
                  <div key={r.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <div>
                      <div style={{ fontSize:13, color:'#fff', fontWeight:500 }}>{r.title}</div>
                      <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:2 }}>{(r.positionsRequired||[]).join(', ')} · {r.applicationCount} applications · {fmtDate(r.createdAt)}</div>
                    </div>
                    <span style={{ fontSize:11, padding:'3px 10px', borderRadius:20, flexShrink:0, background: r.status==='Active'?'rgba(167,244,50,0.12)':'rgba(255,80,80,0.12)', color: r.status==='Active'?'#A7F432':'#ff6b6b' }}>{r.status}</span>
                  </div>
                )) : <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13 }}>No player requests posted</div>}</div>
              )}
              {tab === 'pushed' && (
                <div>{data.pushedPlayers?.length ? data.pushedPlayers.map(p => (
                  <div key={p.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ width:34, height:34, borderRadius:'50%', background:'linear-gradient(135deg,#4B9EF5,#1a5db5)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#fff', flexShrink:0 }}>
                      {p.pfp ? <img src={p.pfp} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials(`${p.firstName||''} ${p.lastName||''}`)}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, color:'#fff', fontWeight:500 }}>{p.firstName} {p.lastName}</div>
                      <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{p.position} → {p.clubName||'Unknown club'}</div>
                    </div>
                    <span style={{ fontSize:11, padding:'3px 10px', borderRadius:20, flexShrink:0, background: p.status==='accepted'?'rgba(167,244,50,0.12)':p.status==='pending'?'rgba(245,166,35,0.12)':'rgba(255,80,80,0.12)', color: p.status==='accepted'?'#A7F432':p.status==='pending'?'#F5A623':'#ff6b6b' }}>{p.status}</span>
                  </div>
                )) : <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13 }}>No players pushed to clubs</div>}</div>
              )}
              {tab === 'shortlisted' && (
                <div>{data.shortlistedPlayers?.length ? data.shortlistedPlayers.map(p => (
                  <div key={p.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ width:34, height:34, borderRadius:'50%', background:'linear-gradient(135deg,#9B59B6,#6c3483)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#fff', flexShrink:0 }}>
                      {p.pfp ? <img src={p.pfp} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials(`${p.firstName||''} ${p.lastName||''}`)}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, color:'#fff', fontWeight:500 }}>{p.firstName} {p.lastName}</div>
                      <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{p.position} · {p.nationality} · {p.requestTitle}</div>
                    </div>
                    <div style={{ fontSize:11, color:'rgba(255,255,255,0.3)' }}>{fmtDate(p.createdAt)}</div>
                  </div>
                )) : <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13 }}>No shortlisted players</div>}</div>
              )}
              {tab === 'players' && (
                <div>{data.managedPlayers?.length ? data.managedPlayers.map(p => (
                  <div key={p.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <div style={{ width:34, height:34, borderRadius:'50%', background: p.pfp?'transparent':'linear-gradient(135deg,#A7F432,#5a9e1a)', overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:700, color:'#000', flexShrink:0 }}>
                      {p.pfp ? <img src={p.pfp} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : initials(`${p.firstName||''} ${p.lastName||''}`)}
                    </div>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:13, color:'#fff', fontWeight:500 }}>{p.firstName} {p.lastName}</div>
                      <div style={{ fontSize:11, color:'rgba(255,255,255,0.4)' }}>{p.position} · {p.nationality}</div>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      {p.overallScore && <div style={{ fontSize:13, fontWeight:700, color:'#F5A623' }}>★ {parseFloat(p.overallScore).toFixed(1)}</div>}
                      {p.tierLabel && <div style={{ fontSize:11, color:'rgba(255,255,255,0.3)' }}>{p.tierLabel}</div>}
                    </div>
                  </div>
                )) : <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13 }}>No players managed</div>}</div>
              )}
              {tab === 'payouts' && (
                <div>{data.payouts?.length ? data.payouts.map(p => (
                  <div key={p.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                    <div><div style={{ fontSize:14, fontWeight:600, color:'#fff' }}>{p.currency} {parseFloat(p.amount).toFixed(2)}</div><div style={{ fontSize:11, color:'rgba(255,255,255,0.4)', marginTop:2 }}>{fmtDate(p.createdAt)}</div></div>
                    <span style={{ fontSize:11, padding:'3px 10px', borderRadius:20, background: p.status==='completed'?'rgba(167,244,50,0.12)':p.status==='pending'?'rgba(245,166,35,0.12)':'rgba(255,80,80,0.12)', color: p.status==='completed'?'#A7F432':p.status==='pending'?'#F5A623':'#ff6b6b' }}>{p.status}</span>
                  </div>
                )) : <div style={{ color:'rgba(255,255,255,0.3)', fontSize:13 }}>No payout requests</div>}</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default function Agents({ toast }) {
  const { token } = useApp();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async (p=1, s='') => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/agents?page=${p}&limit=20${s?`&search=${encodeURIComponent(s)}`:''}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await res.json();
      setAgents(d.data||[]); setPagination(d.pagination||null);
    } catch(e) { toast?.(e.message,'error'); } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(1,''); }, [load]);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div><h1 style={{ fontFamily:'var(--font-display)', fontSize:22, color:'var(--text-primary)', margin:0 }}>Agents</h1><p style={{ color:'var(--text-dim)', fontSize:13, margin:'4px 0 0' }}>Licensed agents on TransferExpo</p></div>
        {pagination && <div style={{ fontSize:12, color:'var(--text-dim)', background:'var(--bg-card)', padding:'6px 14px', borderRadius:8, border:'1px solid var(--border)' }}>{pagination.total} agents</div>}
      </div>
      <form className="toolbar" onSubmit={e => { e.preventDefault(); setPage(1); load(1,search); }} style={{ marginBottom:16 }}>
        <input className="search-input" placeholder="Search by name or email..." value={search} onChange={e=>setSearch(e.target.value)} />
        <button type="submit" className="btn btn-ghost">Search</button>
        {search && <button type="button" className="btn btn-ghost" onClick={() => { setSearch(''); load(1,''); }}>Clear</button>}
      </form>
      <div className="card" style={{ padding:0 }}>
        <div style={{ padding:'14px 20px', borderBottom:'1px solid var(--border)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <span className="card-title">All Agents</span>
          {pagination && <span style={{ fontSize:12, color:'var(--text-dim)' }}>Page {pagination.page} of {pagination.totalPages} · {pagination.total} total</span>}
        </div>
        {loading ? <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>Loading agents...</div>
        : agents.length === 0 ? <div style={{ padding:60, textAlign:'center', color:'var(--text-dim)', fontSize:13 }}>No agents found</div>
        : (
          <div className="table-wrap"><table>
            <thead><tr><th>Agent</th><th>Nationality</th><th>License</th><th>Status</th><th>Joined</th><th></th></tr></thead>
            <tbody>{agents.map(a => {
              const name = a.name || `${a.firstName||''} ${a.lastName||''}`.trim();
              return (
                <tr key={a.id}>
                  <td><div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:32, height:32, borderRadius:'50%', flexShrink:0, overflow:'hidden', display:'flex', alignItems:'center', justifyContent:'center', background:'linear-gradient(135deg,#F5A623,#c47d0a)', fontSize:11, fontWeight:700, color:'#000' }}>{a.pfp?<img src={a.pfp} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>:initials(name)}</div>
                    <div><div style={{ fontWeight:500, fontSize:13 }}>{name}</div><div style={{ fontSize:11, color:'var(--text-dim)' }}>{a.email}</div></div>
                  </div></td>
                  <td style={{ fontSize:12, color:'var(--text-muted)' }}>{a.nationality||'—'}</td>
                  <td><span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background: a.licenseVerificationStatus==='Verified'?'rgba(167,244,50,0.12)':'rgba(255,165,0,0.12)', color: a.licenseVerificationStatus==='Verified'?'#A7F432':'#FFA500' }}>{a.licenseVerificationStatus||'Unknown'}</span></td>
                  <td><span style={{ fontSize:11, padding:'2px 8px', borderRadius:20, background: a.isActive?'rgba(167,244,50,0.12)':'rgba(255,80,80,0.12)', color: a.isActive?'#A7F432':'#ff6b6b' }}>{a.isActive?'Active':'Suspended'}</span></td>
                  <td style={{ fontSize:12, color:'var(--text-dim)' }}>{fmtDate(a.createdAt)}</td>
                  <td><button className="btn btn-ghost" style={{ fontSize:11, padding:'5px 12px' }} onClick={() => setSelectedId(a.id)}>View</button></td>
                </tr>
              );
            })}</tbody>
          </table></div>
        )}
      </div>
      {pagination && pagination.totalPages > 1 && (
        <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:16 }}>
          <button className="btn btn-ghost" disabled={page<=1} onClick={() => { const np=page-1; setPage(np); load(np,search); }}>Prev</button>
          <span style={{ padding:'8px 14px', fontSize:13, color:'var(--text-muted)' }}>Page {page} / {pagination.totalPages}</span>
          <button className="btn btn-ghost" disabled={page>=pagination.totalPages} onClick={() => { const np=page+1; setPage(np); load(np,search); }}>Next</button>
        </div>
      )}
      {selectedId && <AgentModal id={selectedId} token={token} onClose={() => setSelectedId(null)} onDelete={() => load(page, search)} toast={toast} />}
    </div>
  );
}
